import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Car,
  ChevronRight,
  ShieldCheck,
  Fuel,
  Gauge,
  MapPin,
  Building2,
  Sparkles,
  ArrowRight,
  Layers,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useMarketplaceListings, useCarBrands } from '@/modules/cars/hooks/use-cars'

const FALLBACK_BRANDS_LOOKUP: Record<string, { name: string; models: any[] }> = {
  audi: {
    name: 'Audi',
    models: [
      { id: 1, name: 'A6', slug: 'audi-a6', bodyType: 'Sedan' },
      { id: 2, name: 'RS7 Sportback', slug: 'audi-rs7', bodyType: 'Coupe' },
      { id: 3, name: 'Q7', slug: 'audi-q7', bodyType: 'SUV' },
    ],
  },
  bmw: {
    name: 'BMW',
    models: [
      { id: 4, name: '3 Series', slug: 'bmw-3-series', bodyType: 'Sedan' },
      { id: 5, name: 'X5', slug: 'bmw-x5', bodyType: 'SUV' },
      { id: 6, name: 'M4 Competition', slug: 'bmw-m4-competition', bodyType: 'Coupe' },
    ],
  },
  'mercedes-benz': {
    name: 'Mercedes-Benz',
    models: [
      { id: 7, name: 'C-Class', slug: 'mercedes-benz-c-class', bodyType: 'Sedan' },
      { id: 8, name: 'G-Class (G63 AMG)', slug: 'mercedes-benz-g-class', bodyType: 'SUV' },
    ],
  },
  porsche: {
    name: 'Porsche',
    models: [
      { id: 9, name: '911 Carrera / GT3 RS', slug: 'porsche-911', bodyType: 'Coupe' },
    ],
  },
  'land-rover': {
    name: 'Land Rover',
    models: [
      { id: 10, name: 'Defender 110', slug: 'land-rover-defender', bodyType: 'SUV' },
      { id: 11, name: 'Range Rover Velar', slug: 'land-rover-range-rover-velar', bodyType: 'SUV' },
    ],
  },
  mahindra: {
    name: 'Mahindra',
    models: [
      { id: 12, name: 'Thar 4x4', slug: 'mahindra-thar', bodyType: 'SUV' },
      { id: 13, name: 'XUV700', slug: 'mahindra-xuv700', bodyType: 'SUV' },
      { id: 14, name: 'Scorpio-N', slug: 'mahindra-scorpio-n', bodyType: 'SUV' },
    ],
  },
  toyota: {
    name: 'Toyota',
    models: [
      { id: 15, name: 'Fortuner Legender', slug: 'toyota-fortuner', bodyType: 'SUV' },
      { id: 16, name: 'Innova Hycross', slug: 'toyota-innova-hycross', bodyType: 'MUV' },
    ],
  },
  hyundai: {
    name: 'Hyundai',
    models: [
      { id: 17, name: 'Creta', slug: 'hyundai-creta', bodyType: 'SUV' },
      { id: 18, name: 'Verna', slug: 'hyundai-verna', bodyType: 'Sedan' },
    ],
  },
  volkswagen: {
    name: 'Volkswagen',
    models: [
      { id: 19, name: 'Virtus GT', slug: 'volkswagen-virtus', bodyType: 'Sedan' },
    ],
  },
  skoda: {
    name: 'Skoda',
    models: [
      { id: 20, name: 'Slavia TSI', slug: 'skoda-slavia', bodyType: 'Sedan' },
    ],
  },
}

export function CarBrandShowcasePage() {
  const { brandSlug } = useParams<{ brandSlug: string }>()

  // Fetch brand & models
  const { data: dbBrands } = useCarBrands()
  const currentBrand = dbBrands?.find((b: any) => b.slug === brandSlug)

  // Fetch listings for this brand
  const { data: marketplaceData, isLoading } = useMarketplaceListings({
    brandSlug: brandSlug || undefined,
    limit: 24,
  })

  const listings = marketplaceData?.items || []

  const fallbackData = brandSlug ? FALLBACK_BRANDS_LOOKUP[brandSlug] : null
  const brandName = currentBrand?.name || fallbackData?.name || brandSlug?.replace(/-/g, ' ').toUpperCase() || 'LUXURY BRAND'
  const brandModels = (currentBrand?.models && currentBrand.models.length > 0)
    ? currentBrand.models
    : (fallbackData?.models || [])

  const formatCurrency = (amount?: number | null) => {
    if (!amount) return 'Price On Call'
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} Lakh`
    return `₹${amount.toLocaleString('en-IN')}`
  }

  return (
    <div className="min-h-screen space-y-12 pb-16">
      {/* BRAND HERO SECTION */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-900 via-slate-900 to-background text-white pt-12 pb-16 border-b border-border/40">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#CF2F2F]/20 via-transparent to-transparent opacity-60" />

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10 space-y-6">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-xs font-semibold text-slate-400">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="size-3" />
            <Link to="/cars" className="hover:text-white transition-colors">Marketplace</Link>
            <ChevronRight className="size-3" />
            <span className="text-[#CF2F2F] font-bold uppercase">{brandName}</span>
          </nav>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pt-2">
            <div className="space-y-4 max-w-3xl">
              <div className="flex flex-wrap items-center gap-3">
                <Badge className="bg-[#CF2F2F] text-white hover:bg-[#CF2F2F] px-3 py-1 font-bold text-xs uppercase tracking-wider">
                  <Sparkles className="size-3.5 mr-1" /> Official Brand Page
                </Badge>
                <Badge variant="outline" className="text-emerald-400 border-emerald-500/30 bg-emerald-500/10 font-bold text-xs">
                  <ShieldCheck className="size-3.5 mr-1" /> Certified Dealers Inventory
                </Badge>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight text-white font-display">
                {brandName}
              </h1>

              <p className="text-slate-300 text-sm sm:text-base font-sans leading-relaxed">
                Discover the complete lineup of verified pre-owned & new <strong className="text-white">{brandName}</strong> luxury vehicles listed by authorized dealerships across India.
              </p>
            </div>

            <div className="bg-slate-800/80 backdrop-blur-md rounded-2xl p-6 border border-slate-700/60 shrink-0 text-center space-y-2 min-w-[220px]">
              <span className="text-xs uppercase font-bold text-slate-400 block">Total Active Units</span>
              <span className="text-3xl font-extrabold text-[#CF2F2F]">{listings.length}</span>
              <span className="text-[11px] text-slate-300 block font-semibold">Available for Sale</span>
            </div>
          </div>
        </div>
      </section>

      {/* BRAND MODELS LINEUP SECTION */}
      {brandModels.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-6">
          <div className="flex items-center justify-between border-b border-border pb-4">
            <h2 className="text-2xl font-extrabold uppercase tracking-tight text-foreground font-display flex items-center gap-2">
              <Layers className="size-6 text-[#CF2F2F]" /> {brandName} Model Lineup
            </h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {brandModels.map((mod: any) => (
              <Link
                key={mod.id || mod.slug}
                to={`/cars/models/${mod.slug}`}
                className="group p-4 rounded-xl border border-border bg-card hover:border-[#CF2F2F] hover:shadow-lg transition-all text-center space-y-2 block"
              >
                <div className="size-10 rounded-full bg-[#CF2F2F]/10 text-[#CF2F2F] mx-auto flex items-center justify-center font-extrabold text-sm group-hover:scale-110 transition-transform">
                  <Car className="size-5" />
                </div>
                <h3 className="text-sm font-bold text-foreground group-hover:text-[#CF2F2F] transition-colors truncate">
                  {mod.name}
                </h3>
                {mod.bodyType && (
                  <span className="text-[10px] uppercase font-bold text-muted-foreground block">
                    {mod.bodyType}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* BRAND LISTINGS MARKETPLACE GRID */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-4">
          <div>
            <h2 className="text-2xl font-extrabold uppercase tracking-tight text-foreground font-display flex items-center gap-2">
              <Car className="size-6 text-[#CF2F2F]" /> Published {brandName} Cars ({listings.length})
            </h2>
            <p className="text-xs text-muted-foreground">
              Browse all pre-owned & new vehicles listed by authorized dealers.
            </p>
          </div>
          <Link to="/cars">
            <Button variant="outline" size="sm">
              All Cars Marketplace <ArrowRight className="size-4 ml-1" />
            </Button>
          </Link>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((n) => (
              <div key={n} className="h-80 rounded-2xl bg-border/40 animate-pulse" />
            ))}
          </div>
        ) : listings.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map((car: any) => (
              <motion.div
                key={car.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="group rounded-2xl border border-border bg-card overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col"
              >
                {/* Car Image Header */}
                <div className="relative h-48 overflow-hidden bg-slate-900">
                  <img
                    src={car.images?.[0]?.url || '/images/hero-car.png'}
                    alt={car.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-md px-2.5 py-1 rounded-full text-white font-extrabold text-xs">
                    {formatCurrency(car.askingPrice)}
                  </div>
                  <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-md px-2.5 py-0.5 rounded-md text-white text-[11px] font-bold">
                    {car.manufactureYear}
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 space-y-3 flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="text-base font-bold text-foreground line-clamp-1 group-hover:text-[#CF2F2F] transition-colors">
                      {car.title}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                      <MapPin className="size-3.5 text-[#CF2F2F]" />
                      <span>{car.locationCity || 'India'}</span>
                      <span>•</span>
                      <span>{car.mileageKm ? `${car.mileageKm.toLocaleString('en-IN')} km` : 'Unused'}</span>
                    </div>
                  </div>

                  {/* Specs Pill bar */}
                  <div className="grid grid-cols-2 gap-2 text-[11px] font-semibold bg-muted/40 p-2 rounded-lg">
                    <div className="flex items-center gap-1.5 text-foreground/80">
                      <Fuel className="size-3.5 text-amber-500" />
                      <span>{car.fuelType || 'Petrol'}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-foreground/80">
                      <Gauge className="size-3.5 text-blue-500" />
                      <span>{car.transmission || 'Automatic'}</span>
                    </div>
                  </div>

                  {/* Seller / Dealer Info */}
                  <div className="pt-2 border-t border-border/60 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs font-semibold text-foreground/80">
                      <Building2 className="size-4 text-[#CF2F2F]" />
                      <span className="truncate max-w-[120px]">{car.user?.name || 'Verified Merchant'}</span>
                    </div>

                    <Link to={`/cars/${car.slug}`}>
                      <Button size="sm" className="bg-[#CF2F2F] hover:bg-[#CF2F2F]/90 text-white font-bold">
                        Inspect <ChevronRight className="size-3.5 ml-0.5" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-muted/20 rounded-2xl border border-dashed border-border space-y-4">
            <Car className="size-12 mx-auto text-muted-foreground opacity-50" />
            <h3 className="text-lg font-bold text-foreground">No active listings for {brandName} right now</h3>
            <p className="text-xs text-muted-foreground max-w-md mx-auto">
              Check back soon as dealers frequently update their inventory or browse other brands.
            </p>
          </div>
        )}
      </section>
    </div>
  )
}
